import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import Drawer from "../../../Component/Drawer";
import Pill from "../../../Component/Pill";
import { getRequest } from "../../../utils/api";
import { logout } from "../../../utils/auth";
import { SETTLE_STATUS, affiliateName, clientName, inr, nameInitials } from "./AffiliateLayout";

interface AffiliateDrawerProps {
  affiliateId: string | null;
  // bump to reload after a change made from the drawer (tier, payout)
  refreshKey?: number;
  onClose: () => void;
  onChangeTier: (affiliate: any) => void;
  onCreatePayout: (affiliate: any) => void;
  onLogin: (affiliateId: string) => void;
}

// Affiliate profile, earnings, bank details and latest activity (/affiliate/affiliate-details/:id)
const AffiliateDrawer = ({ affiliateId, refreshKey, onClose, onChangeTier, onCreatePayout, onLogin }: AffiliateDrawerProps) => {
  const navigate = useNavigate();
  const [details, setDetails] = useState<any>(null);
  const [isLoader, setIsLoader] = useState(false);

  // Fetch Affiliate Details
  useEffect(() => {
    if (!affiliateId) { setDetails(null); return; }
    const getDetails = async () => {
      try {
        setIsLoader(true);
        const response = await getRequest(`/affiliate/affiliate-details/${affiliateId}`);
        if (response.status === "success") {
          setDetails(response.data);
        } else if (response.status === "session_expired") {
          logout();
        } else {
          toast.error(response.message);
          onClose();
        }
      } catch (error) {
        toast.error("Something went wrong loading the affiliate!");
      } finally {
        setIsLoader(false);
      }
    };
    getDetails();
  }, [affiliateId, refreshKey]);

  const a = details?.affiliate;
  const s = details?.stats || {};
  const bank = details?.bank;
  const tier = a?.affiliate_referral_commission_id;
  const name = a ? affiliateName(a) : "Loading…";

  return (
    <Drawer
      show={!!affiliateId}
      onClose={onClose}
      lead={<div className="cx-who"><div className="av" style={{ width: 44, height: 44, fontSize: 14 }}>{nameInitials(name)}</div></div>}
      title={name}
      subtitle={a ? <><span className="mono">#{a.affiliate_unique_id || a.affiliate_id}</span>{a.affiliate_referral_code ? <> · code <span className="mono">{a.affiliate_referral_code}</span></> : null}</> : null}
      footer={a && <>
        <button type="button" className="cx-btn cx-btn-light" onClick={() => onLogin(a._id)}><i className="ri-login-circle-line"></i>Login as affiliate</button>
        <div className="sp"></div>
        <button type="button" className="cx-btn cx-btn-light" onClick={() => onChangeTier(a)}><i className="ri-percent-line"></i>Change tier</button>
        {/* <button type="button" className="cx-btn cx-btn-primary" disabled={!s.pendingCommission} onClick={() => onCreatePayout(a)}>
          <i className="ri-bank-line"></i>Create payout
        </button> */}
      </>}
    >
      {isLoader && !a ? (
        <div className="cx-empty"><i className="ri-loader-4-line"></i>Loading affiliate…</div>
      ) : a && <>
        <div className="cx-kv k3">
          <div><small>Link clicks</small><b className="mono">{Number(s.clicks || 0).toLocaleString("en-IN")}</b></div>
          <div><small>Referred clients</small><b className="mono">{s.totalClients || 0}</b></div>
          <div><small>WABA connected</small><b className="mono">{s.convertedClients || 0}</b></div>
          <div><small>Total earned</small><b className="mono">{inr(s.totalCommission)}</b></div>
          <div><small>Pending</small><b className={`mono ${s.pendingCommission ? "down" : ""}`}>{inr(s.pendingCommission)}</b></div>
          <div><small>Paid out</small><b className="mono up">{inr(s.paidCommission)}</b></div>
        </div>

        <h6 className="cx-h6">Profile</h6>
        <div className="cx-kv">
          <div><small>Email</small><b style={{ wordBreak: "break-all" }}>{a.affiliate_email}</b></div>
          <div><small>Mobile</small><b className="mono">{a.affiliate_mobile ? `${a.affiliate_country_code || ""} ${a.affiliate_mobile}`.trim() : "—"}</b></div>
          <div><small>Company</small><b>{a.affiliate_companyName || "—"}</b></div>
          <div><small>Website</small><b style={{ wordBreak: "break-all" }}>{a.affiliate_website || "—"}</b></div>
          <div><small>Commission tier</small><b>{tier ? `${tier.commission_title} · ${tier.commission_amount}% plans · ${Number(tier.commission_messaging_amount || 0)}% messaging` : "Not set"}</b></div>
          <div><small>Joined</small><b>{moment(a.createdAt).format("DD MMM YYYY")}</b></div>
          <div><small>Email verified</small><b>{a.affiliate_mail_verification_status ? "Yes" : "No"}</b></div>
          <div><small>Sales from referrals</small><b className="mono">{inr(s.totalSales)}</b></div>
        </div>

        <h6 className="cx-h6">Bank details</h6>
        {bank ? (
          <div className="cx-kv">
            <div><small>Account holder</small><b>{bank.bank_account_holder_name}</b></div>
            <div><small>Bank</small><b>{bank.bank_name}</b></div>
            <div><small>Account number</small><b className="mono">{bank.bank_account_no}</b></div>
            <div><small>IFSC</small><b className="mono">{bank.bank_ifsc_code}</b></div>
          </div>
        ) : (
          <div className="cx-banner" style={{ marginBottom: 0 }}>
            <i className="ri-bank-line"></i>
            <div>No bank details yet. The affiliate adds them from their panel — payouts can't be transferred until then.</div>
          </div>
        )}

        <h6 className="cx-h6" style={{ display: "flex", justifyContent: "space-between" }}>
          Latest commissions
          {details.recentCommissions?.length > 0 && (
            <button type="button" className="cx-link fs12" onClick={() => navigate(`/affiliate-program/commission?search=${encodeURIComponent(a.affiliate_email)}`)}>View all</button>
          )}
        </h6>
        {details.recentCommissions?.length ? details.recentCommissions.map((c: any) => {
          const [tone, label] = SETTLE_STATUS[c.ac_status] || ["none", "Unknown"];
          return (
            <div className="cx-orow" key={c._id}>
              <div className="cx-ic blue"><i className="ri-exchange-dollar-line"></i></div>
              <div>
                <div className="t">{inr(c.ac_amount)} · {clientName(c.ac_user_id)}</div>
                <div className="d"><span className="mono">#{c.ac_unique_id}</span> · {moment(c.createdAt).format("DD MMM YYYY")}</div>
              </div>
              <div className="right"><Pill tone={tone}>{label}</Pill></div>
            </div>
          );
        }) : <p className="fs12 muted">No commissions yet.</p>}

        <h6 className="cx-h6" style={{ display: "flex", justifyContent: "space-between" }}>
          Latest payouts
          {details.recentPayouts?.length > 0 && (
            <button type="button" className="cx-link fs12" onClick={() => navigate(`/affiliate-program/payout?search=${encodeURIComponent(a.affiliate_email)}`)}>View all</button>
          )}
        </h6>
        {details.recentPayouts?.length ? details.recentPayouts.map((p: any) => {
          const [tone, label] = SETTLE_STATUS[p.ap_status] || ["none", "Unknown"];
          return (
            <div className="cx-orow" key={p._id}>
              <div className={`cx-ic ${tone === "none" ? "blue" : tone}`}><i className="ri-bank-line"></i></div>
              <div>
                <div className="t">{inr(p.ap_amount)}</div>
                <div className="d"><span className="mono">#{p.ap_unique_id}</span> · {p.ap_week != null ? `Week ${p.ap_week}, ${p.ap_year} · ` : ""}{moment(p.createdAt).format("DD MMM YYYY")}</div>
              </div>
              <div className="right"><Pill tone={tone}>{label}</Pill></div>
            </div>
          );
        }) : <p className="fs12 muted">No payouts yet.</p>}
      </>}
    </Drawer>
  );
};

export default AffiliateDrawer;
